import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, Plus, Save, Trash2, X, Loader2, GripVertical } from "lucide-react";

function SubneedEditor({ value, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) { setDraft(""); return; }
    onChange([...value, v]);
    setDraft("");
  };
  return (
    <div data-testid="subneed-editor">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((s) => (
          <span key={s} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-orange-50 border border-orange-200 text-xs font-medium text-[#172A3A]" data-testid={`subneed-chip-${s}`}>
            {s}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== s))} className="w-4 h-4 grid place-items-center rounded-full hover:bg-orange-200 text-slate-500" data-testid={`subneed-remove-${s}`}><X size={11} /></button>
          </span>
        ))}
        {value.length === 0 && <span className="text-xs text-slate-400 italic">Aucun sous-besoin — ajoutez-en ci-dessous.</span>}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Nouveau sous-besoin (ex. Recherche de fuite)" className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#E05638]" data-testid="subneed-input" />
        <button type="button" onClick={add} className="px-3 h-9 rounded-lg bg-[#172A3A] text-white text-sm font-semibold inline-flex items-center gap-1" data-testid="subneed-add"><Plus size={14} /> Ajouter</button>
      </div>
    </div>
  );
}

function TradeCard({ trade, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    name: trade.name, icon: trade.icon || "Wrench", active: trade.active !== false,
    sort_order: trade.sort_order ?? 100, subneeds: trade.subneeds || [],
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Le nom du métier est requis.");
    setSaving(true);
    try {
      await api.put(`/devis/admin/trades/${trade.id}`, form);
      toast.success(`Métier « ${form.name} » enregistré.`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Désactiver le métier « ${trade.name} » ? Il ne sera plus proposé aux particuliers.`)) return;
    try { await api.delete(`/devis/admin/trades/${trade.id}`); toast.success("Métier désactivé."); onDeleted(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className={`bg-white rounded-2xl border p-5 ${form.active ? "border-slate-100" : "border-slate-200 opacity-70"}`} data-testid={`trade-card-${trade.slug}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <GripVertical size={16} className="text-slate-300 shrink-0" />
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="font-heading text-lg font-bold text-[#172A3A] bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#E05638] outline-none flex-1 min-w-0" data-testid={`trade-name-${trade.slug}`} />
          <span className="text-xs text-slate-400 shrink-0">/{trade.slug}</span>
        </div>
        <button onClick={del} className="text-slate-300 hover:text-red-500 shrink-0" data-testid={`trade-delete-${trade.slug}`}><Trash2 size={16} /></button>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Sous-besoins (étape 2 du formulaire)</div>
      <SubneedEditor value={form.subneeds} onChange={(v) => setForm((f) => ({ ...f, subneeds: v }))} />

      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-50">
        <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-[#E05638]" data-testid={`trade-active-${trade.slug}`} /> Actif
        </label>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          Ordre <input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))} className="w-16 h-8 px-2 rounded-lg border border-slate-200 text-sm" data-testid={`trade-order-${trade.slug}`} />
        </label>
        <button onClick={save} disabled={saving} className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white bg-[#E05638] hover:bg-[#c7482d] disabled:opacity-50" data-testid={`trade-save-${trade.slug}`}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
        </button>
      </div>
    </div>
  );
}

export default function AdminQuotesTrades() {
  const [trades, setTrades] = useState(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/devis/admin/trades").then((r) => setTrades(r.data.trades || [])).catch((e) => { toast.error(formatApiError(e)); setTrades([]); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return toast.error("Indiquez le nom du métier.");
    setCreating(true);
    try {
      await api.post("/devis/admin/trades", { name: newName.trim(), subneeds: ["Autre demande"] });
      toast.success("Métier créé."); setNewName(""); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setCreating(false); }
  };

  if (trades === null) return <div className="p-10 text-center text-slate-400"><Loader2 className="animate-spin inline" /> Chargement…</div>;

  return (
    <div data-testid="admin-quotes-trades">
      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-5 flex flex-wrap items-center gap-2" data-testid="trade-create">
        <Wrench size={18} className="text-[#E05638]" />
        <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="Ajouter un métier (ex. Vitrerie)" className="flex-1 min-w-[180px] h-10 px-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-[#E05638]" data-testid="trade-create-name" />
        <button onClick={create} disabled={creating} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white bg-[#172A3A] hover:bg-[#0f1c28] disabled:opacity-50" data-testid="trade-create-btn"><Plus size={15} /> Ajouter le métier</button>
      </div>
      <div className="space-y-3">
        {trades.map((t) => <TradeCard key={t.id} trade={t} onSaved={load} onDeleted={load} />)}
      </div>
    </div>
  );
}
